import React from 'react';
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { SelectField } from '../../components/shared/SelectField';
import { SelectItem } from '../../components/ui/select';
import { ScrollArea } from '../../components/ui/scroll-area';

/** Radix Select has no empty option value, so "no filter" needs a sentinel. */
const ANY = '__any__';

/**
 * Filter fields the drawer edits, matching the Advanced Filters pattern used
 * by Assets/Licenses/Cloud (`CloudAdvancedFilters`, `AssetAdvancedFilters`) —
 * search stays on the toolbar, everything else lives here behind the Filters
 * button so the activity table gets the same drawer + removable-chips UX as
 * every other list page instead of its own inline filter row.
 */
export interface AuditFilterState {
  action?: string;
  entityType?: string;
  startDate?: string;
  endDate?: string;
}

export const EMPTY_AUDIT_FILTERS: AuditFilterState = {};

interface AuditAdvancedFiltersProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** In-progress edits, owned by the page's table-state hook. */
  draft: AuditFilterState;
  onDraftChange: (filters: AuditFilterState) => void;
  onApply: () => void;
  onClear: () => void;
  /** Distinct actions seen in the currently loaded activity, e.g. "create", "issue". */
  actionOptions: string[];
  /** Distinct entity types seen in the currently loaded activity, e.g. "asset", "cloud_resource". */
  entityTypeOptions: string[];
}

export const AuditAdvancedFilters: React.FC<AuditAdvancedFiltersProps> = ({
  open,
  onOpenChange,
  draft,
  onDraftChange,
  onApply,
  onClear,
  actionOptions,
  entityTypeOptions,
}) => {
  // The draft lives in the page's table-state hook, which resets it to the
  // applied filters whenever the drawer opens — so editing here never
  // mutates the list until "Apply Filters" commits it.
  const set = <K extends keyof AuditFilterState>(key: K, value: AuditFilterState[K]) => {
    onDraftChange({ ...draft, [key]: value });
  };

  const handleApply = () => {
    onApply();
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>Advanced Filters</SheetTitle>
        </SheetHeader>

        <ScrollArea className="flex-1 min-h-0">
          <div className="space-y-6 px-6 py-5">
            <div className="space-y-4">
              <h4 className="text-sm font-semibold">Activity</h4>
              <div className="space-y-1.5">
                <Label htmlFor="afilter-action">Action</Label>
                <SelectField
                  value={draft.action ?? ANY}
                  onValueChange={(value) => set('action', value === ANY ? undefined : value)}
                  placeholder="Any action"
                >
                  <SelectItem value={ANY}>Any action</SelectItem>
                  {actionOptions.map((action) => (
                    <SelectItem key={action} value={action} className="capitalize">
                      {action.replace(/_/g, ' ')}
                    </SelectItem>
                  ))}
                </SelectField>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="afilter-entity-type">Entity Type</Label>
                <SelectField
                  value={draft.entityType ?? ANY}
                  onValueChange={(value) => set('entityType', value === ANY ? undefined : value)}
                  placeholder="Any entity type"
                >
                  <SelectItem value={ANY}>Any entity type</SelectItem>
                  {entityTypeOptions.map((type) => (
                    <SelectItem key={type} value={type} className="capitalize">
                      {type.replace(/_/g, ' ')}
                    </SelectItem>
                  ))}
                </SelectField>
              </div>
            </div>

            <div className="space-y-4 border-t pt-5">
              <h4 className="text-sm font-semibold">Date Range</h4>
              <div className="space-y-1.5">
                <Label htmlFor="afilter-start">Start Date</Label>
                <Input
                  id="afilter-start"
                  type="date"
                  value={draft.startDate ?? ''}
                  onChange={(e) => set('startDate', e.target.value || undefined)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="afilter-end">End Date</Label>
                <Input
                  id="afilter-end"
                  type="date"
                  value={draft.endDate ?? ''}
                  onChange={(e) => set('endDate', e.target.value || undefined)}
                />
              </div>
            </div>
          </div>
        </ScrollArea>

        <SheetFooter>
          <Button type="button" variant="outline" onClick={onClear}>
            Clear All
          </Button>
          <Button type="button" onClick={handleApply}>
            Apply Filters
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default AuditAdvancedFilters;
